
import { defineComponent, ref, useFetch, watch } from '@nuxtjs/composition-api'
import {
  LoanCalculatorPartner,
  CalculatorItem,
  Slider,
  SliderItem
} from '~/composables/useInstallmentCalculator/useInstallmentCalculator'
import { useInstallmentCalculator } from '~/composables/useInstallmentCalculator'
import { useI18n } from '~/helpers/hooks/usei18n'
import { safeComputed } from '~/modules/helpers'

export default defineComponent({
  components: {
    SliderRange: () => import('~/components/molecules/SliderRange.vue'),
    ExpandableItem: () => import('~/components/molecules/ExpandableItem.vue'),
    Selector: () => import('~/components/atoms/Selector.vue'),
    ProductPrice: () => import('~/modules/catalog/aatrium/components/ProductPrice.vue')
  },
  props: {
    price: {
      type: Number,
      required: true
    }
  },
  setup (props) {
    const i18n = useI18n()
    const {
      fetch: loadPaymentPartners,
      data: loadedPartners,
      depositConfig,
      generateDepositItems,
      interestConfig,
      generateInterestItems,
      periodConfig,
      generatePeriodItems,
      getMonthlyPayment
    } = useInstallmentCalculator()
    const partnerSelectorValue = ref<LoanCalculatorPartner>(null)
    const total = ref<number>(props.price)
    const isSlidersActive = safeComputed<boolean>(() => total.value !== props.price)

    const sliders = safeComputed<Slider[]>(() => [
      {
        value: null,
        title: SliderItem.PERIOD,
        placeholder: 'Select period',
        unit: 'month',
        config: periodConfig,
        selectorItems: generatePeriodItems()
      }, {
        value: null,
        title: SliderItem.DEPOSIT,
        placeholder: 'Select deposit',
        unit: '%',
        config: depositConfig,
        selectorItems: generateDepositItems(props.price)
      }, {
        value: null,
        title: SliderItem.INTEREST,
        placeholder: 'Select interest rate',
        unit: '%',
        config: interestConfig,
        selectorItems: generateInterestItems()
      }
    ])

    function changeSliderValue (key: string, value: CalculatorItem): void {
      const index: number = sliders.value.findIndex(slider => slider.title === key)
      if (index === -1) return

      sliders.value[index].value = value
    }

    const partnerSelectorItems = safeComputed(() => {
      return loadedPartners.value
        ?.filter(partner => !!partner.loan_calculator)
        ?.map((partner) => {
          return {
            value: partner.partner_name,
            description: i18n.tc('Up to {p} month • {r}%', partner.period, { p: partner.period, r: partner.interest_rate }),
            period: String(partner.period),
            interest: String(partner.interest_rate)
          }
        }) || []
    })

    useFetch(async () => {
      await loadPaymentPartners()
    })

    watch(partnerSelectorValue, (partner: LoanCalculatorPartner) => {
      if (partner === null) return
      const { interest, period } = partner
      changeSliderValue(SliderItem.INTEREST, { value: interest })
      changeSliderValue(SliderItem.PERIOD, { value: period })
    })

    watch(sliders, (newSliders: Slider[]) => {
      const slidersValues = newSliders.map(slider => ({ value: +slider.value?.value || 0, unit: slider.unit }))
      const [{ value: period }, { value: deposit }, { value: interest }] = slidersValues
      const validCalculator: boolean = Boolean(interest && period)
      total.value = validCalculator
        ? getMonthlyPayment(props.price, {
          deposit,
          interest,
          period
        })
        : props.price

      if (partnerSelectorValue.value) {
        const selectorPeriod = Number(partnerSelectorValue.value.period)
        const selectorInterest = Number(partnerSelectorValue.value.interest)
        if (selectorPeriod !== period || selectorInterest !== interest) {
          partnerSelectorValue.value = null
        }
      }
    }, { deep: true, immediate: true })

    return {
      partnerSelectorItems,
      partnerSelectorValue,

      isSlidersActive,
      sliders,
      total
    }
  }
})
